import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { useViewAs } from "@/providers/ViewAsProvider";

export type AppRole = 'admin' | 'moderator' | 'user' | 'viewer' | 'child';

async function fetchMyRoles() {
  const { data, error } = await supabase.from('user_roles').select('role');
  if (error) throw error;
  return (data ?? []).map(r => r.role as AppRole);
}

export const RouteGuard = ({ children, requiredRoles }: { children: ReactNode; requiredRoles?: AppRole[] }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const { viewAsRole, isViewingAs, actualRoles } = useViewAs();

  const { data: roles = [], isLoading } = useQuery({
    queryKey: ['my-roles'],
    queryFn: fetchMyRoles,
    enabled: !!user,
    retry: 2,
  });

  // Use viewAsRole if admin is viewing as another role
  const effectiveRoles = isViewingAs ? [viewAsRole!] : roles;

  if (loading || isLoading) {
    return (
      <main className="flex min-h-[60vh] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Checking access…</p>
        </div>
      </main>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace state={{ from: location }} />;
  }


  if (requiredRoles && !requiredRoles.some(r => effectiveRoles.includes(r))) {
    // Log failed admin access attempt (fire and forget)
    supabase.from('admin_access_attempts').insert({
      user_id: user.id,
      attempted_action: `access_route_${location.pathname}`,
      ip_address: 'client',
      blocked_reason: `Required roles: ${requiredRoles.join(', ')}. User has: ${effectiveRoles.join(', ') || 'none'}${isViewingAs ? ` (viewing as ${viewAsRole})` : ''}`
    }).then(({ error }) => {
      if (error) console.error('Failed to log access attempt:', error);
    });
    
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};
